import { notifyOnError } from '~/rage/decorators';
import { IObservable, SeenTracker, Subject } from '~/rage/utils';
import { ClothesType, Gender, IClientConfigs, WalletType } from '~/shared';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { IPlayerService } from '../PlayerService';
import { IRpcService } from '../RpcService';
import { VarsService } from '../VarsService';
import {
	ClothesShopCategoryIndex,
	IClothesShopItem,
	IClothesShopService,
} from './clothesShopServiceTypes';

// Отделяем слои от категории, чтобы иметь возможность легко поменять категории
const clothesTypeToCategoryIndex: Record<
	ClothesType,
	ClothesShopCategoryIndex
> = {
	[ClothesType.TOP]: ClothesShopCategoryIndex.TOP,
	[ClothesType.PANTS]: ClothesShopCategoryIndex.PANTS,
	[ClothesType.SHOES]: ClothesShopCategoryIndex.SHOES,
	[ClothesType.MASK]: ClothesShopCategoryIndex.MASK,
	[ClothesType.HAT]: ClothesShopCategoryIndex.HAT,
	[ClothesType.GLASSES]: ClothesShopCategoryIndex.GLASSES,
	[ClothesType.BACKPACK]: ClothesShopCategoryIndex.BACKPACK,
	[ClothesType.ACCESSORY]: ClothesShopCategoryIndex.ACCESSORIES,
	[ClothesType.SUIT]: ClothesShopCategoryIndex.OUTFIT,
	[ClothesType.EARRINGS]: ClothesShopCategoryIndex.EARS,
	[ClothesType.WATCHES]: ClothesShopCategoryIndex.WATCHES,
	[ClothesType.BRACELET]: ClothesShopCategoryIndex.BRACELETS,
	[ClothesType.ARMOR]: ClothesShopCategoryIndex.ARMOR,
};

export class ClothesShopService implements IClothesShopService {
	private readonly INITIAL_SHOP_GROUP = 'initial';

	private shopItemsByGroup = new Map<string, Subject<IClothesShopItem[]>>([
		[this.INITIAL_SHOP_GROUP, new Subject([] as IClothesShopItem[])],
	]);
	private shopItemsById = new Map<string, IClothesShopItem>();

	private wearingItemsIds = new Subject<string[]>([]);
	private seenTracker = new SeenTracker('clothes_shop');

	constructor(
		private readonly rpc: IRpcService,
		private readonly backend: BackendService,
		private readonly gameConfig: GameConfigService,
		private readonly player: IPlayerService,
		private readonly vars: VarsService,
	) {
		this.backend.addEventHandler(
			'clothes:update',
			(data: { clothes: string[] }) => {
				this.wearingItemsIds.next(data.clothes);
			},
		);

		this.gameConfig.getConfigs().subscribe(() => {
			this.updateShopItems();
		});

		this.player.getPlayerGender().subscribe(() => {
			this.updateShopItems();
		});
	}

	private async updateShopItems(): Promise<void> {
		await this.gameConfig.waitForInit();

		const configItems = this.gameConfig.configs?.clothes?.items ?? [];
		const combinedItems = new Map<string, IClientConfigs['clothes']['items']>();

		// Группируем предметы с одинаковой моделью, чтобы показывать их в магазине как один предмет
		for (const item of configItems) {
			if (!item.shopGroup) {
				continue;
			}

			const key = `${item.gender}_${item.componentId}_${item.drawableId}`;

			let items = combinedItems.get(key);
			if (!items) {
				items = [];
				combinedItems.set(key, items);
			}

			items.push(item);
			const shopItem = this.getShopItemFromConfigItem(item);
			if (shopItem) {
				this.shopItemsById.set(item.id, shopItem);
			}
		}

		const shopItemsByGroup = new Map<string, IClothesShopItem[]>();
		for (const items of combinedItems.values()) {
			// Сначала оставляем только предметы, которые попадают в магазин
			const suitableItems = items.filter((shopItem) => {
				if (!this.shopItemsById.get(shopItem.id)) {
					return false;
				}

				if (
					shopItem.gender !== Gender.COMMON &&
					shopItem.gender !== this.player.getPlayerGender().getValue()
				) {
					return false;
				}

				return true;
			});

			// Потом группируем
			for (let i = 0; i < suitableItems.length; i++) {
				const shopItem = this.shopItemsById.get(suitableItems[i].id)!;

				if (i > 0) {
					shopItem.parentItemId = suitableItems[0].id;
				}

				const shopGroup = suitableItems[i].shopGroup!;
				let itemsByGroup = shopItemsByGroup.get(shopGroup);
				if (!itemsByGroup) {
					itemsByGroup = [];
					shopItemsByGroup.set(shopGroup, itemsByGroup);
				}

				itemsByGroup.push(shopItem);
			}
		}

		for (const [group, items] of shopItemsByGroup) {
			if (this.shopItemsByGroup.has(group)) {
				this.shopItemsByGroup.get(group)!.next(items);
			} else {
				this.shopItemsByGroup.set(group, new Subject(items));
			}
		}

		// Чистить не обязательно, т к группы не будут часто меняться
	}

	private getShopItemFromConfigItem(
		item: IClientConfigs['clothes']['items'][number],
	): IClothesShopItem | null {
		if (!item.type) {
			return null;
		}

		return {
			id: item.id,
			name: item.name,
			price: item.price!,
			wallet: WalletType.Cash,
			category: clothesTypeToCategoryIndex[item.type],
			parentItemId: null,
			gender: item.gender,
		};
	}

	private getCurrentShopGroup(): IObservable<string> {
		return this.vars.get('clothesShop:shopGroup', this.INITIAL_SHOP_GROUP);
	}

	public getShopItems(): IObservable<IClothesShopItem[]> {
		const group = this.getCurrentShopGroup().getValue();

		const subj = this.shopItemsByGroup.get(group);
		const fallbackSubj = this.shopItemsByGroup.get(this.INITIAL_SHOP_GROUP)!;
		return subj ?? fallbackSubj;
	}

	public getShopItemById(itemId: string): IClothesShopItem | null {
		return this.shopItemsById.get(itemId) || null;
	}

	public getWearingItems(): IObservable<string[]> {
		return this.wearingItemsIds;
	}

	public async previewItem(itemId: string): Promise<void> {
		return await this.rpc.invokeClient('clothesShop:preview', itemId);
	}

	public async clearPreview(): Promise<void> {
		return await this.rpc.invokeClient('clothesShop:clearPreview');
	}

	public async exitShop(): Promise<void> {
		return await this.rpc.invokeClient('clothesShop:exit');
	}

	@notifyOnError
	public async buy(itemId: string, wallet: WalletType): Promise<void> {
		await this.backend.invoke('clothes:buy', { itemId, wallet });
	}

	public isUnseen(path: string): boolean {
		return this.seenTracker.isUnseen(path);
	}

	public markSeen(path: string): void {
		this.seenTracker.markAsSeen(path);
	}
}
